import React, { useRef, useState, useCallback } from "react";
import {
  DmsPage,
  DmsTable,
  DmsAltBox,
  DmsPermissions,
  intl,
  fetchData,
  HTTP_STATUS
} from "dmsbasereact";
import { Chip, Typography, IconButton, Tooltip, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Swal from "sweetalert2";
import { InstitutionTypeEnum } from "../../constants/InstitutionType";
import { Permissions } from "../../constants/Permissions";

export const SAPHomologatedInstitutionsPage = () => {
  const table = useRef(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleActivate = useCallback((row) => {
    Swal.fire({
      title: intl.get("ConfirmActivation").d("Confirm Activation"),
      text: intl.get("ConfirmActivateInstitutionSAP").d("Are you sure you want to activate this institution in SAP?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.get("Yes").d("Yes"),
      cancelButtonText: intl.get("Cancel").d("Cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setActionLoading(true);
          const response = await fetchData(
            `/sap-institutions/${row.thing_pk}/activate`,
            "POST",
            true
          );
          if (response.status === HTTP_STATUS.OK_200) {
            Swal.fire({
              icon: "success",
              title: intl.get("InstitutionActivatedSAP").d("Institution Activated"),
              text: intl.get("InstitutionActivatedSAPMessage").d("Institution added to the buyer list in SAP."),
            });
            table.current?.updateTable();
          } else {
            Swal.fire({
              icon: "error",
              title: intl.get("Error").d("Error"),
              text: intl.get("FailedToAddInstitutionSAP").d("Failed to add institution to the buyer list in SAP."),
            });
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: intl.get("Error").d("Error"),
            text: intl.get("FailedToAddInstitutionSAP").d("Failed to add institution to the buyer list in SAP."),
          });
        } finally {
          setActionLoading(false);
        }
      }
    });
  }, []);

  const handleDeactivate = useCallback((row) => {
    Swal.fire({
      title: intl.get("ConfirmDeactivation").d("Confirm Deactivation"),
      text: intl.get("ConfirmDeactivateInstitutionSAP").d("Are you sure you want to deactivate this institution from SAP?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.get("Yes").d("Yes"),
      cancelButtonText: intl.get("Cancel").d("Cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setActionLoading(true);
          const response = await fetchData(
            `/sap-institutions/${row.thing_pk}/deactivate`,
            "POST",
            true
          );
          if (response.status === HTTP_STATUS.OK_200) {
            Swal.fire({
              icon: "success",
              title: intl.get("InstitutionDeactivatedSAP").d("Institution Deactivated"),
              text: intl.get("InstitutionDeactivatedSAPMessage").d("Institution removed from the buyer list in SAP."),
            });
            table.current?.updateTable();
          } else {
            Swal.fire({
              icon: "error",
              title: intl.get("Error").d("Error"),
              text: intl.get("FailedToRemoveInstitutionSAP").d("Failed to remove institution from the buyer list in SAP."),
            });
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: intl.get("Error").d("Error"),
            text: intl.get("FailedToRemoveInstitutionSAP").d("Failed to remove institution from the buyer list in SAP."),
          });
        } finally {
          setActionLoading(false);
        }
      }
    });
  }, []);

  const getCategoryLabel = (categoryName) => {
    const entry = InstitutionTypeEnum[categoryName];
    if (entry) return entry.label;
    if (categoryName === "NONE") return intl.get("NoneLabel").d("None");
    return categoryName;
  };

  const categoryOptions = Object.values(InstitutionTypeEnum).map((type) => ({
    value: type.value,
    label: type.label,
  }));

  const header = [
    {
      label: intl.get("InstitutionName").d("Institution Name"),
      column_name: "name",
      sortable: true,
      filter: true
    },
    {
      label: intl.get("CNPJ").d("CNPJ"),
      column_name: "cnpj",
      sortable: true,
      filter: true
    },
    {
      label: intl.get("SAPClientCode").d("SAP Client Code"),
      column_name: "foreign_register",
      sortable: true,
      filter: true,
      element: (row) => row.foreign_register || "-"
    },
    {
      label: intl.get("Category").d("Category"),
      column_name: "category_name",
      sortable: true,
      filter: true,
      control: "select",
      options: categoryOptions,
      element: (row) => {
        const names = row.category_names?.length ? row.category_names : [row.category_name];
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {names.map((name) => (
              <Chip key={name} label={getCategoryLabel(name)} size="small" variant="outlined" />
            ))}
          </Box>
        );
      }
    },
    {
      label: intl.get("Email").d("Email"),
      column_name: "email_address",
      sortable: true,
      element: (row) => row.email_address || "-"
    },
    {
      label: intl.get("Phone").d("Phone"),
      column_name: "phone_number",
      sortable: true,
      element: (row) => row.phone_number || "-"
    },
    {
      label: intl.get("HomologatedAt").d("Homologated At"),
      column_name: "sap_homologated_at",
      type: "date",
      sortable: true,
      element: (row) => {
        if (!row.sap_homologated_at) return "-";
        try {
          const date = new Date(row.sap_homologated_at);
          return date.toLocaleDateString("pt-BR");
        } catch {
          return row.sap_homologated_at;
        }
      }
    },
    {
      label: intl.get("Status").d("Status"),
      column_name: "sap_homologated",
      sortable: false,
      element: (row) => (
        <Chip
          label={
            row.sap_homologated
              ? intl.get("Active").d("Active")
              : intl.get("Inactive").d("Inactive")
          }
          color={row.sap_homologated ? "success" : "default"}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      sortable: false,
      element: (row) => {
        return (
          <DmsPermissions permissions={[Permissions.SAP_HOMOLOGATED_INSTITUTIONS_MANAGE]}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {row.sap_homologated ? (
                <Tooltip title={intl.get("Deactivate").d("Deactivate")}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeactivate(row)}
                    disabled={actionLoading}
                  >
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={intl.get("Activate").d("Activate")}>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleActivate(row)}
                    disabled={actionLoading}
                  >
                    <CheckCircleIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </DmsPermissions>
        );
      }
    }
  ];

  return (
    <DmsPage name={intl.get("SAPHomologatedInstitutions").d("SAP Homologated Institutions")}>
      <DmsAltBox.Card>
        <DmsAltBox.Header>
          <Typography variant='h3' sx={{ fontSize: '1.1rem' }}>
            {intl.get("SAPHomologatedInstitutions").d("SAP Homologated Institutions")}
          </Typography>
        </DmsAltBox.Header>
        <DmsAltBox.Body>
          <DmsTable
            ref={table}
            header={header}
            url="/sap-institutions/table"
            managed={true}
            needs_authentication={true}
            export_table={false}
          />
        </DmsAltBox.Body>
      </DmsAltBox.Card>
    </DmsPage>
  );
};
